/**
 * TemplateCard — compact list-view row used by the templates editor route.
 *
 * Sits between ``<SurfaceList>`` (the chrome) and ``<TemplateDetail>``
 * (the editor form). Each card surfaces:
 *   - name + description
 *   - scope chip (BUILT-IN / USER / ORG / PUBLIC)
 *   - thumbnail when ``preview_url`` is set
 *
 * Active-row chrome reuses the ``StudioItem``-style orange-soft pill so
 * the affordance stays consistent across the drawer + the editor.
 */

import type * as React from "react";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import type { TemplateSummary } from "@ando/core";
import { cn } from "@ando/ui";

const SCOPE_CHIP: Record<TemplateSummary["scope"], { label: ReturnType<typeof msg>; cls: string }> =
  {
    builtin: {
      label: msg`BUILT-IN`,
      cls: "bg-ink-1/[0.05] text-ink-3",
    },
    user: {
      label: msg`USER`,
      cls: "bg-ando-orange-soft text-ando-orange",
    },
    org: {
      label: msg`ORG`,
      cls: "bg-amber-500/15 text-amber-700",
    },
    public: {
      label: msg`PUBBLICO`,
      cls: "bg-success/15 text-success",
    },
  };

export interface TemplateCardProps {
  readonly template: TemplateSummary;
  readonly active?: boolean;
  readonly onSelect?: () => void;
}

export const TemplateCard: React.FC<TemplateCardProps> = ({ template, active, onSelect }) => {
  const { i18n } = useLingui();
  const scope = SCOPE_CHIP[template.scope];
  return (
    <button
      type="button"
      onClick={onSelect}
      data-active={active ? "true" : undefined}
      className={cn(
        "group flex w-full items-start gap-3 rounded-lg border border-hairline/70 bg-card/55 px-3 py-2.5 text-left transition-colors",
        "hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
        active && "border-accent/40 bg-ando-orange-soft hover:bg-ando-orange-soft",
      )}
    >
      {template.preview_url ? (
        <img
          src={template.preview_url}
          alt=""
          aria-hidden
          className="h-10 w-14 shrink-0 rounded border border-hairline/40 bg-white object-cover"
        />
      ) : (
        <span
          aria-hidden
          className="h-10 w-14 shrink-0 rounded border border-hairline/40 bg-gradient-to-br from-ando-orange-soft to-amber-500/20"
        />
      )}
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate font-medium text-[13px] text-ink-1">{template.name}</span>
          <span
            className={cn(
              "shrink-0 rounded px-1.5 py-0.5 font-mono text-[9px] tracking-wider",
              scope.cls,
            )}
          >
            {i18n._(scope.label)}
          </span>
        </span>
        <span className="mt-0.5 line-clamp-2 block text-[11.5px] text-ink-3 leading-snug">
          {template.description}
        </span>
      </span>
    </button>
  );
};
TemplateCard.displayName = "TemplateCard";
